// The organization's credits as the toolbar shows them (GET /auth/credits; backend/app/core/metering.py).
// Nothing is shown where compute is not charged: a desktop, or a server not metering yet.
import type { CreditLot, CreditStatus, CreditTransaction } from "../api/types";

/** Pages that spend credits fire this, so the toolbar re-reads the balance at once rather than at its next poll. */
export const CREDITS_CHANGED = "insiso:credits-changed";
export const creditsChanged = (): void => { try { window.dispatchEvent(new Event(CREDITS_CHANGED)); } catch { /* no window */ } };

export type CreditLevel = "ok" | "low" | "empty";

/** Amber and red only mean something once compute is charged. */
export const creditLevel = (c: CreditStatus): CreditLevel => (!c.metering ? "ok" : c.empty ? "empty" : c.low ? "low" : "ok");

/** The toolbar's label, or null where there are no credits (a desktop). */
export function creditText(c: CreditStatus | null | undefined): string | null {
  if (!c || !c.shown) return null;
  if (c.metering && c.empty) return "Out of credits";
  const n = c.balance.toLocaleString("en-US");
  return `${n} credit${c.balance === 1 ? "" : "s"}${c.held > 0 ? ` (+${c.held.toLocaleString("en-US")} reserved)` : ""}`;
}

/** The toolbar's tooltip: what the number means and what to do when it runs low. */
export function creditTip(c: CreditStatus): string {
  if (!c.metering) return "Credits pay for training, auto-annotation and model exports. Nothing is charged yet.";
  const base = "Credits pay for training, auto-annotation and model exports.";
  const held = c.held > 0 ? ` ${c.held.toLocaleString("en-US")} are reserved for training still running; what it does not use comes back.` : "";
  const level = creditLevel(c);
  const act = level === "empty" ? " Training, auto-annotation and exports are paused until your administrator adds credits."
    : level === "low" ? " Most of your credits are used: ask your administrator to add more." : "";
  return base + held + act;
}

const SOURCES: Record<CreditLot["source"], string> = { monthly: "Monthly allowance", grant: "Granted by InSiSo", coupon: "Coupon code" };
export const lotSource = (l: Pick<CreditLot, "source">): string => SOURCES[l.source] ?? l.source;
/** When a lot lapses: the allowance at the end of its month, a grant after its days, or never. */
export const lotExpiry = (l: Pick<CreditLot, "expires_at">): string => (l.expires_at ? l.expires_at.slice(0, 10) : "never");

const OPERATIONS: Record<string, string> = {
  auto_annotate_image: "Auto-annotation", train_epoch_100_images: "Training", export_onnx: "ONNX export",
  export_nvidia: "NVIDIA export", export_hailo: "Hailo export", export_dataset_gb: "Dataset export",
  augment_image: "Augmented images",
};
/** One ledger row in words: what moved the balance and how. */
export function txnText(t: Pick<CreditTransaction, "kind" | "operation" | "quantity" | "reference_type" | "reference_id">): string {
  const what = OPERATIONS[t.operation] ?? t.operation;
  const images = (t.operation === "auto_annotate_image" || t.operation === "augment_image") && t.quantity ? ` (${t.quantity} image${t.quantity === 1 ? "" : "s"})` : "";
  const job = (t.reference_type === "training_job" || t.reference_type === "augment_job") && t.reference_id ? ` #${t.reference_id}` : "";
  switch (t.kind) {
    case "grant": return "Credits received";
    case "expire": return "Credits lapsed";
    case "hold": return `${what}${job}: set aside`;
    case "settle": return `${what}${job}: settled, unused part returned`;
    default: return `${what}${images}`;
  }
}
/** A signed amount, as the ledger shows it: +500, -12. */
export const signed = (n: number): string => `${n > 0 ? "+" : ""}${n.toLocaleString("en-US")}`;

/** The CSV handed to a reseller: one row per code, saying what it gives. */
export function couponCsv(b: { gives: string; reseller: string; reference: string; codes_expire_at: string | null }, codes: string[]): string {
  const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const ends = b.codes_expire_at ? b.codes_expire_at.slice(0, 10) : "never";
  return ["code,gives,reseller,reference,expires", ...codes.map((c) => [c, b.gives, b.reseller, b.reference, ends].map(cell).join(","))].join("\n") + "\n";
}
